"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NetRequirement = exports.LocPaybackAmount = exports.LocPaymentAmount = exports.LocDisbursement = exports.PeriodicRate = exports.DrawAmount = exports.AnnualPercentageRate = exports.CentsPerDollar = exports.CostOfCapital = exports.DrawFee = exports.FinalPaymentAmount = exports.TotalInterest = exports.EffectivePaymentCount = exports.PaymentRate = exports.DailyRate = exports.AnnualRate = exports.PayoffMonths = exports.StandardDisbursement = exports.Commission = exports.StandardPaymentAmount = exports.NumPayments = exports.StandardPayback = exports.Factor = exports.BuyRate = exports.OriginationAmount = exports.OriginationPercent = exports.Points = exports.StandardAmount = exports.Term = void 0;
const lodash_1 = require("lodash");
const rounding_1 = require("../rounding");
const ApprovalCalculator_utility_1 = require("./ApprovalCalculator.utility");
const general_formulas_1 = require("./formulas/general-formulas");
const functional_approval_1 = require("./functional-approval");
const approval_attribute_1 = require("./types/approval-attribute");
exports.Term = {
    alias: 'term',
    label: 'Term',
    description: 'Term length of Loan/Approval',
    source: approval_attribute_1.ApprovalAttributeSources.userInput,
    dynamicType: approval_attribute_1.DynamicApprovalAttributeTypes.numericSet,
    incrementSize: 1,
    initialValueResolver: (functionalApproval) => {
        return functionalApproval.selectedOption.term;
    },
    globalBoundariesResolver: (functionalApproval) => {
        const options = functionalApproval.approval.options;
        const optionValues = options.map(opt => opt.term);
        return {
            min: (0, lodash_1.min)(optionValues),
            max: (0, lodash_1.max)(optionValues),
        };
    },
    currentValueResolver: (functionalApproval) => {
        return functionalApproval.selectedOption.term;
    },
    optionsResolver: (functionalApproval) => {
        const options = functionalApproval.approval.options;
        const values = options.map(opt => opt.term);
        const term = functionalApproval.selectedOption.term;
        switch (functionalApproval.approval.calculationType) {
            case 'standard':
            case 'maxPayment':
            case 'loc':
                //@ts-ignore
                return [...new Set(values).values()];
            case 'term':
            case 'sba':
                return Array.from({ length: term }, (__, i) => i + 1);
        }
    },
};
exports.StandardAmount = {
    alias: 'amount',
    label: 'Amount',
    description: 'Amount of Loan/Approval',
    source: approval_attribute_1.ApprovalAttributeSources.userInput,
    dynamicType: approval_attribute_1.DynamicApprovalAttributeTypes.range,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.currency,
    incrementSize: 1000,
    initialValueResolver: (functionalApproval) => {
        const maxOption = functionalApproval.maxOption;
        const factorAttribute = functionalApproval.attributesMap.factor;
        const initialMaxFactorRate = factorAttribute.initialValueResolver(functionalApproval);
        return (0, ApprovalCalculator_utility_1.getOptionAvailableAmount)(maxOption, functionalApproval.approval.calculationType, initialMaxFactorRate, functionalApproval.approval.loanProductRoundingStrategy);
    },
    currentBoundariesResolver: (functionalApproval) => {
        const currentOption = functionalApproval.selectedOption;
        const factor = functionalApproval.getAttributeCurrentValue('factor');
        const max = (0, ApprovalCalculator_utility_1.getOptionAvailableAmount)(currentOption, functionalApproval.approval.calculationType, factor, functionalApproval.approval.loanProductRoundingStrategy);
        return {
            min: currentOption.limits.amount.min,
            max,
        };
    },
    globalBoundariesResolver: (functionalApproval) => {
        return (0, functional_approval_1.getAttributeGlobalBoundaries)(functionalApproval, 'limits.amount.min', 'limits.amount.max');
    },
};
exports.Points = {
    alias: 'points',
    label: 'Points',
    description: 'Lendio points (representing our commission taken on the Deal)',
    source: approval_attribute_1.ApprovalAttributeSources.userInput,
    dynamicType: approval_attribute_1.DynamicApprovalAttributeTypes.range,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.number,
    incrementSize: 1,
    initialValueResolver: (functionalApproval) => {
        const currentOption = functionalApproval.maxOption;
        return currentOption.limits.points.max;
    },
    currentBoundariesResolver: (functionalApproval) => {
        const currentOption = functionalApproval.selectedOption;
        return {
            min: currentOption.limits.points.min,
            max: currentOption.limits.points.max,
        };
    },
    globalBoundariesResolver: (functionalApproval) => {
        return (0, functional_approval_1.getAttributeGlobalBoundaries)(functionalApproval, 'limits.points.min', 'limits.points.max');
    },
};
exports.OriginationPercent = {
    alias: 'originationPercent',
    label: 'Origination %',
    description: 'Percentage of Loan Amount to be applied as the Origination fee',
    source: approval_attribute_1.ApprovalAttributeSources.userInput,
    dynamicType: approval_attribute_1.DynamicApprovalAttributeTypes.range,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.percent,
    incrementSize: .1,
    initialValueResolver: (functionalApproval) => {
        functionalApproval.setCurrentOptionOriginationParams();
        return functionalApproval.originationParams.originationPercent.absMax;
    },
    currentBoundariesResolver: (functionalApproval) => {
        functionalApproval.setCurrentOptionOriginationParams();
        const selectedOption = functionalApproval.selectedOption;
        const currentAmount = functionalApproval.getAttributeCurrentValue('amount');
        const originationParams = (0, ApprovalCalculator_utility_1.getOriginationParams)(selectedOption.limits, currentAmount);
        return {
            min: originationParams.originationPercent.min,
            max: originationParams.originationPercent.max,
        };
    },
    globalBoundariesResolver: (functionalApproval) => {
        functionalApproval.setCurrentOptionOriginationParams();
        const selectedOption = functionalApproval.selectedOption;
        const currentAmount = functionalApproval.getAttributeCurrentValue('amount');
        const originationParams = (0, ApprovalCalculator_utility_1.getOriginationParams)(selectedOption.limits, currentAmount);
        return {
            min: originationParams.originationPercent.absMin,
            max: originationParams.originationPercent.absMax,
        };
    }
};
exports.OriginationAmount = {
    alias: 'originationAmount',
    label: 'Origination Fee',
    description: 'Origination fee amount which is either provided as a flat value, or calculated as a percentage of the Loan Amount.',
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.currency,
    incrementSize: 100,
    initialValueResolver: (functionalApproval) => {
        functionalApproval.setCurrentOptionOriginationParams();
        return functionalApproval.originationParams.originationAmount.absMax;
    },
    currentBoundariesResolver: (functionalApproval) => {
        return {
            min: functionalApproval.originationParams.originationAmount.min,
            max: functionalApproval.originationParams.originationAmount.max,
        };
    },
    globalBoundariesResolver: (functionalApproval) => {
        return {
            min: functionalApproval.originationParams.originationAmount.absMin,
            max: functionalApproval.originationParams.originationAmount.absMax,
        };
    },
    currentValueResolver: (functionalApproval) => {
        functionalApproval.setCurrentOptionOriginationParams();
        const amount = functionalApproval.getAttributeCurrentValue('amount');
        const originationPercent = functionalApproval.getAttributeCurrentValue('originationPercent');
        return (0, rounding_1.toFixedNumber)((originationPercent / 100) * amount, 2, 10);
    },
};
exports.BuyRate = {
    alias: 'buyRate',
    label: 'Buy Rate',
    description: 'The Lender\'s provided interest rate for a given ApprovalOption',
    source: approval_attribute_1.ApprovalAttributeSources.approvalOption,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.number,
    incrementSize: .1,
    initialValueResolver: (functionalApproval) => {
        const { customCalculatorParams } = functionalApproval.maxOption;
        const maxBuyrateConditional = customCalculatorParams === null || customCalculatorParams === void 0 ? void 0 : customCalculatorParams.pointConditionalBuyrateThreshold;
        const maxBuyrateConditional2 = customCalculatorParams === null || customCalculatorParams === void 0 ? void 0 : customCalculatorParams.pointConditionalBuyrateThreshold2;
        const maxOption = functionalApproval.maxOption;
        maxOption.limits.originationPercent.max;
        const maxPoints = maxOption.limits.points.max;
        return (!!maxOption && maxPoints !== undefined && 'buyRate' in maxOption) ?
            functionalApproval.calculator.buyRate(maxOption.buyRate, maxPoints, {
                pointConditionalBuyrateThreshold: maxBuyrateConditional,
                pointConditionalBuyrateThreshold2: maxBuyrateConditional2
            }) : 0;
    },
    currentValueResolver: (functionalApproval) => {
        const selectedOption = functionalApproval.selectedOption;
        const { customCalculatorParams } = selectedOption;
        const buyRateBase = selectedOption.buyRate;
        const selectedPoints = functionalApproval.getAttributeCurrentValue('points');
        // if there are
        return functionalApproval.calculator.buyRate(buyRateBase, selectedPoints, Object.assign({}, customCalculatorParams));
    }
};
exports.Factor = {
    alias: 'factor',
    label: 'Factor Rate',
    description: 'The combined interest from the Lender`\s provided Buy Rate and Lendio\'s points. To be used as the functional interest rate for the loan.',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.number,
    incrementSize: .1,
    initialValueResolver: (functionalApproval) => {
        const buyRateAttribute = functionalApproval.attributesMap.buyRate;
        const initialMaxBuyRate = buyRateAttribute.initialValueResolver(functionalApproval);
        const maxOption = functionalApproval.maxOption;
        const maxPoints = maxOption.limits.points.max;
        const maxOriginationPercent = maxOption.limits.originationPercent.max;
        return (0, ApprovalCalculator_utility_1.getFactorRate)(functionalApproval.approval.pointsTradable, initialMaxBuyRate, maxPoints, maxOriginationPercent, maxOriginationPercent);
    },
    currentValueResolver: (functionalApproval) => {
        const buyRate = functionalApproval.getAttributeCurrentValue('buyRate');
        const points = functionalApproval.getAttributeCurrentValue('points');
        const originationPercentAttribute = functionalApproval.attributesMap.originationPercent;
        const currentOriginationPercent = originationPercentAttribute.currentValue, maxOriginationPercent = originationPercentAttribute.currentBoundaries.max;
        return (0, ApprovalCalculator_utility_1.getFactorRate)(functionalApproval.approval.pointsTradable, buyRate, points, currentOriginationPercent, maxOriginationPercent);
    },
};
exports.StandardPayback = {
    alias: 'payback',
    label: 'Payback',
    description: 'Total Loan payback amount',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.currency,
    incrementSize: 1000,
    initialValueResolver: (functionalApproval) => {
        const initialFactorAmount = functionalApproval.getAttributeInitialValue('factor');
        const initialAmountValue = functionalApproval.getAttributeInitialValue('amount');
        return (0, ApprovalCalculator_utility_1.getPaybackAmount)(initialFactorAmount, initialAmountValue);
    },
    currentValueResolver: (functionalApproval) => {
        const factor = functionalApproval.getAttributeCurrentValue('factor');
        const amount = functionalApproval.getAttributeCurrentValue('amount');
        return (0, ApprovalCalculator_utility_1.getPaybackAmount)(factor, amount);
    },
};
exports.NumPayments = {
    alias: 'numPayments',
    label: '# Payments',
    description: 'Total number of individual payments to be made over full term of the Loan.',
    source: approval_attribute_1.ApprovalAttributeSources.approvalOption,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.number,
    incrementSize: 1,
    initialValueResolver: (functionalApproval) => {
        return functionalApproval.maxOption.payments;
    },
    currentValueResolver: (functionalApproval) => {
        return functionalApproval.selectedOption.payments;
    },
};
exports.StandardPaymentAmount = {
    alias: 'paymentAmount',
    label: 'Payment amount',
    description: 'Amount of each individual payment to be made throughout the total term of the Loan.',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.currency,
    incrementSize: 1000,
    initialValueResolver: (functionalApproval) => {
        const initialPaybackAmount = functionalApproval.getAttributeInitialValue('payback');
        const initialNumPayments = functionalApproval.getAttributeInitialValue('numPayments');
        return (0, ApprovalCalculator_utility_1.getAchPaymentAmount)(initialPaybackAmount, initialNumPayments);
    },
    currentValueResolver: (functionalApproval) => {
        const payback = functionalApproval.getAttributeCurrentValue('payback');
        const numPayments = functionalApproval.getAttributeCurrentValue('numPayments');
        return (0, ApprovalCalculator_utility_1.getAchPaymentAmount)(payback, numPayments);
    },
};
exports.Commission = {
    alias: 'commission',
    label: 'Commission',
    description: 'Calculated Lendio commission based on Loan Amount and Points',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.currency,
    incrementSize: 1000,
    initialValueResolver: (functionalApproval) => {
        const amountAlias = ['term', 'loc'].includes(functionalApproval.approval.calculationType)
            ? 'drawAmount'
            : 'amount';
        const initialAmountValue = functionalApproval.getAttributeInitialValue(amountAlias);
        const initialPointsValue = functionalApproval.getAttributeInitialValue('points');
        return (0, ApprovalCalculator_utility_1.getCommission)(initialAmountValue, initialPointsValue);
    },
    currentValueResolver: (functionalApproval) => {
        const amountAlias = ['term', 'loc'].includes(functionalApproval.approval.calculationType)
            ? 'drawAmount'
            : 'amount';
        const amount = functionalApproval.getAttributeCurrentValue(amountAlias);
        const points = functionalApproval.getAttributeCurrentValue('points');
        return (0, ApprovalCalculator_utility_1.getCommission)(amount, points);
    },
};
exports.StandardDisbursement = {
    alias: 'disbursement',
    label: 'Disbursement',
    description: 'Calculated Disbursement based on Loan Amount, withheld amount, and origination fees.',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.currency,
    incrementSize: 1000,
    initialValueResolver: (functionalApproval) => {
        const initialAmount = functionalApproval.getAttributeInitialValue('amount');
        const initialOriginationAmount = functionalApproval.getAttributeInitialValue('originationAmount');
        const withheldAmount = functionalApproval.approval.withheldAmount || 0;
        const payoffAmount = functionalApproval.approval.payoffAmount || 0;
        return initialAmount - initialOriginationAmount - withheldAmount - payoffAmount;
    },
    currentValueResolver: (functionalApproval) => {
        const amount = functionalApproval.getAttributeCurrentValue('amount');
        const initialOriginationAmount = functionalApproval.getAttributeCurrentValue('originationAmount');
        const withheldAmount = functionalApproval.approval.withheldAmount || 0;
        const payoffAmount = functionalApproval.approval.payoffAmount || 0;
        return amount - initialOriginationAmount - withheldAmount - payoffAmount;
    },
};
exports.PayoffMonths = {
    alias: 'payoffMonths',
    label: 'Payoff Months',
    description: 'Selected number of months over which to pay down the balance of a line of credit draw',
    dynamicType: approval_attribute_1.DynamicApprovalAttributeTypes.range,
    source: approval_attribute_1.ApprovalAttributeSources.userInput,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.number,
    incrementSize: 1,
    initialValueResolver: (functionalApproval) => {
        return functionalApproval.maxTerm;
    },
    currentBoundariesResolver: (functionalApproval) => {
        const currentTerm = functionalApproval.getAttributeCurrentValue('term');
        return {
            min: 1,
            max: currentTerm,
        };
    },
    globalBoundariesResolver: (functionalApproval) => {
        return {
            min: 1,
            max: functionalApproval.maxTerm,
        };
    },
};
exports.AnnualRate = {
    alias: 'annualRate',
    label: 'Annual Rate',
    description: 'Annual Rate',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.percent,
    incrementSize: .1,
    initialValueResolver: (functionalApproval) => {
        const currentOption = functionalApproval.maxOption;
        const periodicRate = currentOption.periodicRate;
        const periodsPerYear = currentOption.periodsPerYear || 0;
        return (0, general_formulas_1.getAnnualRate)({
            periodicRate,
            periodsPerYear,
        });
    },
    currentValueResolver: (functionalApproval) => {
        const option = functionalApproval.selectedOption;
        const periodicRate = option.periodicRate;
        const periodsPerYear = option.periodsPerYear || 0;
        return (0, general_formulas_1.getAnnualRate)({
            periodicRate,
            periodsPerYear,
        });
    },
};
exports.DailyRate = {
    alias: 'dailyRate',
    label: 'Daily Rate',
    description: 'Daily Rate',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.percent,
    incrementSize: .1,
    initialValueResolver: (functionalApproval) => {
        const annualRate = functionalApproval.getAttributeInitialValue('annualRate');
        return (0, general_formulas_1.getDailyRate)({ annualRate }) || 0;
    },
    currentValueResolver: (functionalApproval) => {
        const annualRate = functionalApproval.getAttributeCurrentValue('annualRate');
        return (0, general_formulas_1.getDailyRate)({ annualRate }) || 0;
    },
};
exports.PaymentRate = {
    alias: 'paymentRate',
    label: 'Payment Rate',
    description: 'Payment Rate',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.percent,
    incrementSize: .1,
    initialValueResolver: (functionalApproval) => {
        const dailyRate = functionalApproval.getAttributeInitialValue('dailyRate');
        const option = functionalApproval.maxOption;
        const daysInPeriod = Number(option.daysInPeriod) || 0;
        return (0, general_formulas_1.getPaymentRate)({
            dailyRate,
            daysInPeriod,
        });
    },
    currentValueResolver: (functionalApproval) => {
        const dailyRate = functionalApproval.getAttributeCurrentValue('dailyRate');
        const option = functionalApproval.selectedOption;
        const daysInPeriod = Number(option.daysInPeriod) || 0;
        return (0, general_formulas_1.getPaymentRate)({
            dailyRate,
            daysInPeriod,
        });
    }
};
exports.EffectivePaymentCount = {
    alias: 'effectivePaymentCount',
    label: 'Effective Payment Count',
    description: 'Effective Payment Count',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.number,
    incrementSize: 1,
    initialValueResolver: (functionalApproval) => {
        const payoffMonths = functionalApproval.getAttributeInitialValue('payoffMonths');
        const numPayments = functionalApproval.getAttributeInitialValue('numPayments');
        const term = functionalApproval.getAttributeInitialValue('term');
        return (0, general_formulas_1.calculateEffectivePaymentCount)(payoffMonths, numPayments, term);
    },
    currentValueResolver: (functionalApproval) => {
        const payoffMonths = functionalApproval.getAttributeCurrentValue('payoffMonths');
        const numPayments = functionalApproval.getAttributeCurrentValue('numPayments');
        const term = functionalApproval.getAttributeCurrentValue('term');
        return (0, general_formulas_1.calculateEffectivePaymentCount)(payoffMonths, numPayments, term);
    },
};
exports.TotalInterest = {
    alias: 'totalInterest',
    label: 'Total Interest',
    description: 'Total Interest',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.currency,
    incrementSize: 1000,
    initialValueResolver: (functionalApproval) => {
        const initialPayoffMonths = functionalApproval.getAttributeInitialValue('payoffMonths');
        const initialNumPayments = functionalApproval.getAttributeInitialValue('numPayments');
        const initialTerm = functionalApproval.getAttributeInitialValue('term');
        return (0, general_formulas_1.findTotalInterest)(functionalApproval.calculator.paymentSchedule, initialPayoffMonths, initialNumPayments, initialTerm);
    },
    currentValueResolver: (functionalApproval) => {
        const payoffMonths = functionalApproval.getAttributeCurrentValue('payoffMonths');
        const numPayments = functionalApproval.getAttributeCurrentValue('numPayments');
        const term = functionalApproval.getAttributeCurrentValue('term');
        functionalApproval.initializeCalculator();
        return (0, general_formulas_1.findTotalInterest)(functionalApproval.calculator.paymentSchedule, payoffMonths, numPayments, term);
    },
};
exports.FinalPaymentAmount = {
    alias: 'finalPaymentAmount',
    label: 'Final Payment Amount',
    description: 'Final Payment Amount',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.currency,
    incrementSize: 100,
    initialValueResolver: (functionalApproval) => {
        return (0, general_formulas_1.findFinalPaymentAmount)(functionalApproval.calculator.paymentSchedule);
    },
    currentValueResolver: (functionalApproval) => {
        functionalApproval.initializeCalculator();
        return (0, general_formulas_1.findFinalPaymentAmount)(functionalApproval.calculator.paymentSchedule);
    },
};
exports.DrawFee = {
    alias: 'drawFee',
    label: 'Draw Fee',
    description: 'Calculated Draw Fee.',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.currency,
    incrementSize: 100,
    initialValueResolver: (functionalApproval) => {
        const amount = functionalApproval.getAttributeInitialValue('drawAmount');
        const option = functionalApproval.maxOption;
        const feeAlias = functionalApproval.approval.calculationType === 'loc' ? 'drawFeePercent' : 'originationFeePercent';
        const drawFeePercent = (0, lodash_1.get)(option, feeAlias);
        return functionalApproval.calculator.drawFeeAmount(amount, drawFeePercent);
    },
    currentValueResolver: (functionalApproval) => {
        const amount = functionalApproval.getAttributeCurrentValue('drawAmount');
        const option = functionalApproval.selectedOption;
        const feeAlias = functionalApproval.approval.calculationType === 'loc' ? 'drawFeePercent' : 'originationFeePercent';
        const drawFeePercent = (0, lodash_1.get)(option, feeAlias);
        return functionalApproval.calculator.drawFeeAmount(amount, drawFeePercent);
    },
};
exports.CostOfCapital = {
    alias: 'costOfCapital',
    label: 'Cost of Capital',
    description: 'Calculated Cost of Capital',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.currency,
    incrementSize: 100,
    initialValueResolver: (functionalApproval) => {
        const totalInterest = functionalApproval.getAttributeInitialValue('totalInterest');
        const drawFee = functionalApproval.getAttributeInitialValue('drawFee');
        return functionalApproval.calculator.totalCostOfCapital(totalInterest, drawFee);
    },
    currentValueResolver: (functionalApproval) => {
        const totalInterest = functionalApproval.getAttributeCurrentValue('totalInterest');
        const drawFee = functionalApproval.getAttributeCurrentValue('drawFee');
        return functionalApproval.calculator.totalCostOfCapital(totalInterest, drawFee);
    },
};
exports.CentsPerDollar = {
    alias: 'centsPerDollar',
    label: 'Cents On The Dollar',
    description: 'Calculated Cents On The Dollar',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.currency,
    incrementSize: 1,
    initialValueResolver: (functionalApproval) => {
        const payback = functionalApproval.getAttributeInitialValue('payback');
        const drawAmount = functionalApproval.getAttributeInitialValue('drawAmount');
        return functionalApproval.calculator.centsOnTheDollar(payback, drawAmount);
    },
    currentValueResolver: (functionalApproval) => {
        const payback = functionalApproval.getAttributeCurrentValue('payback');
        const drawAmount = functionalApproval.getAttributeCurrentValue('drawAmount');
        functionalApproval.initializeCalculator();
        return functionalApproval.calculator.centsOnTheDollar(payback, drawAmount);
    },
};
exports.AnnualPercentageRate = {
    alias: 'apr',
    label: 'Annual Percentage Rate',
    description: 'Calculated Annual Percentage Rate',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.percent,
    incrementSize: .1,
    initialValueResolver: (functionalApproval) => {
        const amount = functionalApproval.getAttributeInitialValue('drawAmount');
        const option = functionalApproval.maxOption;
        const drawFee = functionalApproval.getAttributeInitialValue('drawFee');
        const paymentAmount = functionalApproval.getAttributeInitialValue('paymentAmount');
        const effectivePaymentCount = functionalApproval.getAttributeInitialValue('effectivePaymentCount');
        const periodsPerYear = option.periodsPerYear || 0;
        const finalPaymentAmount = functionalApproval.getAttributeInitialValue('finalPaymentAmount');
        return functionalApproval.calculator.apr(amount, drawFee, paymentAmount, effectivePaymentCount, periodsPerYear, finalPaymentAmount);
    },
    currentValueResolver: (functionalApproval) => {
        const amount = functionalApproval.getAttributeCurrentValue('drawAmount');
        const option = functionalApproval.selectedOption;
        const drawFee = functionalApproval.getAttributeCurrentValue('drawFee');
        const paymentAmount = functionalApproval.getAttributeCurrentValue('paymentAmount');
        const effectivePaymentCount = functionalApproval.getAttributeCurrentValue('effectivePaymentCount');
        const periodsPerYear = option.periodsPerYear || 0;
        const finalPaymentAmount = functionalApproval.getAttributeCurrentValue('finalPaymentAmount');
        return functionalApproval.calculator.apr(amount, drawFee, paymentAmount, effectivePaymentCount, periodsPerYear, finalPaymentAmount);
    },
};
exports.DrawAmount = {
    alias: 'drawAmount',
    label: 'Draw Amount',
    description: 'Selected Draw Amount',
    source: approval_attribute_1.ApprovalAttributeSources.userInput,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.currency,
    dynamicType: approval_attribute_1.DynamicApprovalAttributeTypes.range,
    incrementSize: 1000,
    initialValueResolver: (functionalApproval) => {
        const boundaries = exports.DrawAmount.globalBoundariesResolver(functionalApproval);
        return boundaries.max;
    },
    currentBoundariesResolver: (functionalApproval) => {
        const amountAlias = functionalApproval.approval.calculationType === 'loc' ? 'drawAmount' : 'amount';
        const minPath = `limits.${amountAlias}.min`, maxPath = `limits.${amountAlias}.max`;
        const currentOption = functionalApproval.selectedOption;
        return {
            min: (0, lodash_1.get)(currentOption, minPath),
            max: (0, lodash_1.get)(currentOption, maxPath),
        };
    },
    globalBoundariesResolver: (functionalApproval) => {
        const amountAlias = functionalApproval.approval.calculationType === 'loc' ? 'drawAmount' : 'amount';
        const minPath = `limits.${amountAlias}.min`, maxPath = `limits.${amountAlias}.max`;
        return (0, functional_approval_1.getAttributeGlobalBoundaries)(functionalApproval, minPath, maxPath);
    },
};
exports.PeriodicRate = {
    alias: 'periodicRate',
    label: 'Periodic Rate',
    description: 'Periodic Rate of currently selected Approval Option',
    source: approval_attribute_1.ApprovalAttributeSources.approvalOption,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.percent,
    incrementSize: .1,
    initialValueResolver: (functionalApproval) => {
        const currentOption = functionalApproval.selectedOption;
        return currentOption.periodicRate;
    },
    currentValueResolver: (functionalApproval) => {
        const currentOption = functionalApproval.selectedOption;
        return currentOption.periodicRate;
    },
};
exports.LocDisbursement = {
    alias: 'disbursement',
    label: 'Disbursement',
    description: 'Disbursement',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.currency,
    incrementSize: 1000,
    initialValueResolver: (functionalApproval) => {
        const drawAmount = functionalApproval.getAttributeInitialValue('drawAmount');
        const drawFee = functionalApproval.getAttributeInitialValue('drawFee');
        return functionalApproval.calculator.disbursement(drawAmount, drawFee);
    },
    currentValueResolver: (functionalApproval) => {
        const drawAmount = functionalApproval.getAttributeCurrentValue('drawAmount');
        const drawFee = functionalApproval.getAttributeCurrentValue('drawFee');
        return functionalApproval.calculator.disbursement(drawAmount, drawFee);
    },
};
exports.LocPaymentAmount = {
    alias: 'paymentAmount',
    label: 'Payment Amount',
    description: 'Calculated Payment Amount',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.currency,
    incrementSize: 100,
    initialValueResolver: (functionalApproval) => {
        const currentOption = functionalApproval.selectedOption;
        const drawAmount = functionalApproval.getAttributeInitialValue('drawAmount');
        const periodicRate = functionalApproval.getAttributeInitialValue('periodicRate');
        const effectivePaymentCount = functionalApproval.getAttributeInitialValue('effectivePaymentCount');
        return (0, general_formulas_1.getPaymentAmount)({
            drawAmount,
            periodicRate,
            paymentCount: effectivePaymentCount || currentOption.payments,
        });
    },
    currentValueResolver: (functionalApproval) => {
        const currentOption = functionalApproval.selectedOption;
        const drawAmount = functionalApproval.getAttributeCurrentValue('drawAmount');
        const periodicRate = functionalApproval.getAttributeCurrentValue('periodicRate');
        const effectivePaymentCount = functionalApproval.getAttributeCurrentValue('effectivePaymentCount');
        return (0, general_formulas_1.getPaymentAmount)({
            drawAmount,
            periodicRate,
            paymentCount: effectivePaymentCount || currentOption.payments,
        });
    },
};
exports.LocPaybackAmount = {
    alias: 'payback',
    label: 'Payback Amount',
    description: 'Calculated Payback Amount',
    source: approval_attribute_1.ApprovalAttributeSources.calculation,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.currency,
    incrementSize: 1000,
    initialValueResolver: (functionalApproval) => {
        const drawAmount = functionalApproval.getAttributeInitialValue('drawAmount');
        const totalInterest = functionalApproval.getAttributeInitialValue('totalInterest');
        return functionalApproval.calculator.payback(drawAmount, totalInterest);
    },
    currentValueResolver: (functionalApproval) => {
        const drawAmount = functionalApproval.getAttributeCurrentValue('drawAmount');
        const totalInterest = functionalApproval.getAttributeCurrentValue('totalInterest');
        return functionalApproval.calculator.payback(drawAmount, totalInterest);
    },
};
exports.NetRequirement = {
    alias: 'netRequirement',
    label: 'Net Requirement',
    description: 'Net Requirement',
    source: approval_attribute_1.ApprovalAttributeSources.approval,
    displayType: approval_attribute_1.ApprovalAttributeDisplayTypes.currency,
    incrementSize: 1000,
    initialValueResolver: (functionalApproval) => {
        return functionalApproval.approval.netMinimum;
    },
};
